'use client';

import React from 'react';

import { Button } from '@depop/web-ui-kit/Button';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { ExtendedLink } from '@/components/ExtendedLink';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useCurrentRoute } from '@/modules/activityTracker/useCurrentRoute';
import { fallbackLinks } from '@/modules/appStoreLinks/constants';
import { DeviceType } from '@/modules/device/types';
import { useUserAgent } from '@/modules/device/useUserAgent';
import { useBranchDeepLink } from '@/modules/externalTracking/useBranchDeepLink';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = React.ComponentProps<typeof Button> & {
  label?: string;
  trackingId: string;
  appPath?: string;
  excludeDeeplinkAttribution?: boolean;
  desktopDeepView?: string;
  canonicalUrl?: string;
  onClick?: () => void;
};

export function DownloadAppButton({
  label,
  trackingId: ctaId,
  onClick = () => null,
  appPath = '/',
  excludeDeeplinkAttribution,
  desktopDeepView,
  canonicalUrl,
  ...rest
}: Props) {
  const t = useTranslations('common');
  const { language } = useCurrentLanguage();
  const { device } = useUserAgent();
  const isAndroid = device === DeviceType.ANDROID;
  const fallbackLink =
    fallbackLinks[language][isAndroid ? 'PlayStore' : 'AppStore'];
  const appStoreLink = useBranchDeepLink({
    fallback: canonicalUrl || fallbackLink,
    appPath,
    referer: '',
    stage: 'download app link',
    feature: 'organic',
    channel: 'website',
    campaign: '',
    referrerSource: `download_button__${ctaId}`,
    excludeDeeplinkAttribution,
    desktopDeepView,
  });
  const [, user] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  const urlPath = usePathname();
  const page = useCurrentRoute();

  function handleClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.APP_DOWNLOAD_ACTION, {
      page,
      urlPath,
      ctaId,
      schemaVersion: '1.0',
    });
    onClick();
  }

  return (
    <ExtendedLink href={appStoreLink} onClick={handleClick}>
      <Button as="span" {...rest}>
        {label || t('DownloadTheApp')}
      </Button>
    </ExtendedLink>
  );
}
